import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Copy, Loader2, RefreshCw, Download, Key } from "lucide-react";

function CodesPanel({ codes, onDone }) {
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("All codes copied");
    } catch {
      toast.error("Clipboard blocked — please select and copy the codes manually.");
    }
  };
  const download = () => {
    const blob = new Blob(
      [
        "B&L Rentals — 2FA recovery codes\n",
        "Generated: " + new Date().toISOString() + "\n\n",
        "Each code can be used ONCE if you lose your authenticator app.\n",
        "Store these somewhere safe (password manager, printed copy).\n\n",
        ...codes.map((c) => c + "\n"),
      ],
      { type: "text/plain" },
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "blrentals-2fa-recovery-codes.txt";
    a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="mt-6 rounded-lg border border-[#E2AB20] bg-[#E2AB20]/5 p-5" data-testid="totp-recovery-panel">
      <div className="flex items-center gap-2">
        <Key size={18} className="text-[#E2AB20]" />
        <h3 className="font-heading font-semibold text-white">Save your recovery codes</h3>
      </div>
      <p className="mt-2 text-sm text-[#E5EAF2]">
        These <b>eight one-time codes</b> can each get you back in if you lose your phone. Save them <b>right now</b> — we won't show them again.
      </p>
      <ul className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-sm" data-testid="totp-recovery-list">
        {codes.map((c, i) => (
          <li key={i} data-testid={`recovery-code-${i}`} className="bg-black/30 border border-white/10 rounded px-3 py-2 text-[#E2AB20] text-center">
            {c}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button onClick={copyAll} className="bg-white/5 hover:bg-white/10 text-white border border-white/20" data-testid="totp-copy-codes">
          <Copy size={14} className="mr-2" /> Copy all
        </Button>
        <Button onClick={download} className="bg-white/5 hover:bg-white/10 text-white border border-white/20" data-testid="totp-download-codes">
          <Download size={14} className="mr-2" /> Download .txt
        </Button>
        <Button onClick={onDone} className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold ml-auto" data-testid="totp-codes-done">
          I've saved them — continue
        </Button>
      </div>
    </div>
  );
}

/**
 * Two-Factor Authentication (TOTP) setup panel with recovery codes.
 */
export default function TotpSetup() {
  const [status, setStatus] = useState({ enabled: null, recovery_remaining: 0 });
  const [enroll, setEnroll] = useState(null);
  const [code, setCode] = useState("");
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [regenPw, setRegenPw] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null); // shown once, right after verify/regenerate
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/auth/totp/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/enroll");
      setEnroll(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start enrollment");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/verify", { code });
      toast.success("Two-factor authentication is now ON.");
      setEnroll(null); setCode("");
      setRecoveryCodes(data.recovery_codes || []);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/totp/disable", { password: disablePw, code: disableCode });
      toast.success("Two-factor authentication disabled");
      setDisablePw(""); setDisableCode("");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wrong password or code");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/totp/regenerate-recovery", { password: regenPw, code: regenCode });
      toast.success("New recovery codes generated. Old ones are now invalid.");
      setRegenPw(""); setRegenCode("");
      setRecoveryCodes(data.recovery_codes || []);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wrong password or code");
    } finally { setBusy(false); }
  };

  const copySecret = async () => {
    if (!enroll?.secret) return;
    try {
      await navigator.clipboard.writeText(enroll.secret);
      toast.success("Secret copied");
    } catch {
      toast.error("Clipboard blocked — please copy the secret manually.");
    }
  };

  if (status.enabled === null) {
    return (
      <div className="card-bl p-6 flex items-center gap-3 text-[#AEB9C9]" data-testid="totp-loading">
        <Loader2 size={16} className="animate-spin" /> Checking two-factor status…
      </div>
    );
  }

  return (
    <div className="card-bl p-6 max-w-3xl" data-testid="totp-setup">
      <div className="flex items-center gap-3">
        {status.enabled ? (
          <ShieldCheck size={22} className="text-[#E2AB20]" />
        ) : (
          <ShieldAlert size={22} className="text-red-400" />
        )}
        <div>
          <h2 className="font-heading font-semibold text-white text-lg">Two-factor authentication</h2>
          <p className="text-xs text-[#AEB9C9]">
            {status.enabled
              ? `2FA is ON. ${status.recovery_remaining} recovery code${status.recovery_remaining === 1 ? "" : "s"} left.`
              : "2FA is OFF — your account is protected by password only. Turning this on stops password-only attackers cold."}
          </p>
        </div>
      </div>

      {/* Newly generated recovery codes take priority over any other view */}
      {recoveryCodes && (
        <CodesPanel codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
      )}

      {/* Enabled → show disable + regenerate forms */}
      {status.enabled && !recoveryCodes && (
        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-3" data-testid="totp-disable-form">
            <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">Disable two-factor</div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={disablePw} onChange={(e) => setDisablePw(e.target.value)} data-testid="totp-disable-password" />
            </div>
            <div>
              <Label>Current 6-digit code</Label>
              <Input
                inputMode="numeric" maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="totp-disable-code"
              />
            </div>
            <Button
              onClick={disable}
              disabled={busy || disablePw.length < 6 || disableCode.length !== 6}
              className="bg-red-500/80 hover:bg-red-500 text-white disabled:opacity-50"
              data-testid="totp-disable-btn"
            >
              {busy ? "Disabling…" : "Turn off 2FA"}
            </Button>
          </div>

          <div className="space-y-3" data-testid="totp-regen-form">
            <div className="text-xs uppercase tracking-widest text-[#AEB9C9]">Recovery codes</div>
            <p className="text-xs text-[#AEB9C9]">Running low or worried a code leaked? Rotate them — the old set becomes invalid immediately.</p>
            <div>
              <Label>Password</Label>
              <Input type="password" value={regenPw} onChange={(e) => setRegenPw(e.target.value)} data-testid="totp-regen-password" />
            </div>
            <div>
              <Label>Current 6-digit code</Label>
              <Input
                inputMode="numeric" maxLength={6}
                value={regenCode}
                onChange={(e) => setRegenCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="totp-regen-code"
              />
            </div>
            <Button
              onClick={regenerate}
              disabled={busy || regenPw.length < 6 || regenCode.length !== 6}
              className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold disabled:opacity-50"
              data-testid="totp-regen-btn"
            >
              <RefreshCw size={14} className="mr-2" /> {busy ? "Working…" : "Generate new recovery codes"}
            </Button>
          </div>
        </div>
      )}

      {/* Disabled + not enrolling → show enable button */}
      {!status.enabled && !enroll && !recoveryCodes && (
        <div className="mt-6">
          <Button
            onClick={startEnroll}
            disabled={busy}
            className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold"
            data-testid="totp-enable-btn"
          >
            {busy ? "Loading…" : "Enable two-factor auth"}
          </Button>
        </div>
      )}

      {/* Enrolling → show QR + verify */}
      {!status.enabled && enroll && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6" data-testid="totp-enroll-panel">
          <div className="rounded-lg bg-white p-3 grid place-items-center">
            <img src={enroll.qr_data_url} alt="Scan this QR code with Google Authenticator, 1Password, or Authy" className="w-52 h-52" data-testid="totp-qr" />
          </div>
          <div className="space-y-3">
            <ol className="text-sm text-[#E5EAF2] space-y-1.5 list-decimal list-inside">
              <li>Open Google Authenticator, 1Password, Authy, or any TOTP app on your phone.</li>
              <li>Scan the QR code (or type in the secret below manually).</li>
              <li>Enter the current 6-digit code below to finish setup.</li>
              <li>You'll receive 8 one-time recovery codes — save them!</li>
            </ol>

            <div>
              <Label>Setup secret (manual entry option)</Label>
              <div className="mt-1 flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-white/5 border border-white/10 rounded px-3 py-2 text-[#E2AB20] break-all" data-testid="totp-secret">
                  {enroll.secret}
                </code>
                <Button size="sm" variant="outline" onClick={copySecret} data-testid="totp-copy-secret"><Copy size={14} /></Button>
              </div>
            </div>

            <div>
              <Label>Verification code</Label>
              <Input
                inputMode="numeric" maxLength={6} autoFocus placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="totp-verify-code"
              />
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={verify}
                disabled={busy || code.length !== 6}
                className="bg-[#E2AB20] hover:bg-[#D89B08] text-[#010F27] font-semibold disabled:opacity-50"
                data-testid="totp-verify-btn"
              >
                {busy ? "Verifying…" : "Verify & turn on 2FA"}
              </Button>
              <Button variant="outline" onClick={() => { setEnroll(null); setCode(""); }} data-testid="totp-cancel-btn">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
